import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faVideo, 
  faHeadphones, 
  faFile, 
  faDownload,
  faPlay,
  faPause,
  faClock,
  faLink,
  faTimes,
  faSpinner,
  faArrowUpRightFromSquare,
  faCalendarDays
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';


// A link resource can be a scheduled online meeting: show when it starts, and whether it has passed.
const formatMeetingDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleString('fr-FR', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// A meeting only reads as "passée" once it has had time to happen: going grey a minute after the
// start time would mislabel a session that is still running.
const MEETING_GRACE_MINUTES = 60;

const isMeetingPast = (value) => {
  if (!value) return false;
  const date = new Date(value);
  if (isNaN(date.getTime())) return false;
  return date.getTime() + MEETING_GRACE_MINUTES * 60 * 1000 < Date.now();
};

const ViewFormation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formation, setFormation] = useState(null);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingResource, setPlayingResource] = useState(null);
  const [loadingResource, setLoadingResource] = useState(null);
  const [downloadingResource, setDownloadingResource] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [audioStates, setAudioStates] = useState({}); // { [id]: { playing: false, currentTime: 0, duration: 0 } }
  const [audioLoading, setAudioLoading] = useState({}); // Track which audios are loading/playing to prevent concurrent calls
  const [formationProgress, setFormationProgress] = useState(null);
  const videoRefs = useRef({});
  const audioRefs = useRef({});
  const progressTrackingTimers = useRef({}); // For throttling progress updates
  const videoCurrentTimes = useRef({}); // Store currentTime for videos when paused due to visibility
  const maxVideoTimes = useRef({}); // Track maximum reached time for each video (to prevent skipping forward)
  const maxAudioTimes = useRef({}); // Track maximum reached time for each audio (to prevent skipping forward)
  const audioPlayPromises = useRef({}); // Track play promises to prevent concurrent play/pause calls

  useEffect(() => {
    fetchFormation();
    fetchResources();
    fetchProgress();

    // Cleanup: pause all media when component unmounts
    return () => {
      // Pause all videos and save their currentTime
      Object.keys(videoRefs.current).forEach((videoId) => {
        const video = videoRefs.current[videoId];
        if (video && !video.paused) {
          videoCurrentTimes.current[videoId] = video.currentTime;
          video.pause();
        }
      });
      
      // Pause all audios
      Object.values(audioRefs.current).forEach((audio) => {
        if (audio && !audio.paused) {
          audio.pause();
        }
      });
    };
  }, [id]);

  // Fetch progress on mount and periodically
  useEffect(() => {
    const interval = setInterval(() => {
      fetchProgress();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [id]);

  // Pause video and audio when page becomes hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause all videos and save their currentTime
        Object.keys(videoRefs.current).forEach((videoId) => {
          const video = videoRefs.current[videoId];
          if (video && !video.paused) {
            // Save currentTime before pausing
            videoCurrentTimes.current[videoId] = video.currentTime;
            video.pause();
          }
        });
        
        // Pause all audios
        Object.values(audioRefs.current).forEach((audio) => {
          if (audio && !audio.paused) {
            audio.pause();
            // Update audio state
            const audioId = Object.keys(audioRefs.current).find(
              (id) => audioRefs.current[id] === audio
            );
            if (audioId) {
              setAudioStates(prev => ({
                ...prev,
                [audioId]: { ...prev[audioId], playing: false }
              }));
            }
          }
        });
      } else {
        // Page became visible again - restore video currentTime if saved
        Object.keys(videoRefs.current).forEach((videoId) => {
          const video = videoRefs.current[videoId];
          const savedTime = videoCurrentTimes.current[videoId];
          if (video && savedTime !== undefined && savedTime > 0) {
            // Restore the saved currentTime
            video.currentTime = savedTime;
            // Clear the saved time after restoring
            delete videoCurrentTimes.current[videoId];
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchFormation = async () => {
    try {
      const response = await api.get(`/formations/${id}/view`);
      setFormation(response.data.data);
    } catch (error) {
      console.error('Error fetching formation:', error);
      if (error.response?.status === 403) {
        setError('Vous n\'avez pas accès à cette formation');
      } else if (error.response?.status === 404) {
        setError('Formation non trouvée');
      } else {
        setError('Erreur lors du chargement de la formation');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      const response = await api.get(`/formations/${id}/resources`);
      const resourcesData = response.data.data || [];
      
      // Sort resources: videos first, then audio, then files, then links
      const sortedResources = resourcesData.sort((a, b) => {
        const typeOrder = { video: 1, audio: 2, file: 3, link: 4 };
        const orderA = typeOrder[a.type] || 99;
        const orderB = typeOrder[b.type] || 99;
        
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        
        return (a.order || 0) - (b.order || 0);
      });
      
      setResources(sortedResources);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setError('Erreur lors du chargement des ressources');
    }
  };

  const getStreamUrl = (resourceId) => {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    return `${baseUrl}/formations/${id}/resources/${resourceId}/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  };

  const getDownloadUrl = (resourceId) => {
    return `${process.env.REACT_APP_API_URL}/formations/${id}/resources/${resourceId}/download-proxy`;
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const fetchProgress = async () => {
    try {
      const response = await api.get(`/formations/${id}/progress`);
      if (response.data.success) {
        setFormationProgress(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const trackProgress = async (resourceId, resourceType, data) => {
    try {
      await api.post(`/formations/${id}/resources/${resourceId}/track-progress`, data);
    } catch (error) {
      console.error('Error tracking progress:', error);
    }
  };

  const trackVideoProgress = (resourceId, currentTime, duration) => {
    if (!duration || duration === 0) return;
    
    const percent = Math.round((currentTime / duration) * 100);
    
    // Throttle: only track every 5 seconds or when significant change
    const lastTracked = progressTrackingTimers.current[resourceId];
    const now = Date.now();
    
    if (!lastTracked || (now - lastTracked) > 5000 || percent >= 95) {
      progressTrackingTimers.current[resourceId] = now;
      trackProgress(resourceId, 'video', {
        progress_percent: percent,
        current_time: currentTime,
        duration: duration
      });
    }
  };

  const trackAudioProgress = (resourceId, currentTime, duration) => {
    if (!duration || duration === 0) return;
    
    const percent = Math.round((currentTime / duration) * 100);
    
    // Throttle: only track every 5 seconds or when significant change
    const lastTracked = progressTrackingTimers.current[resourceId];
    const now = Date.now();
    
    if (!lastTracked || (now - lastTracked) > 5000 || percent >= 95) {
      progressTrackingTimers.current[resourceId] = now;
      trackProgress(resourceId, 'audio', {
        progress_percent: percent,
        current_time: currentTime,
        duration: duration
      });
    }
  };

  const handleDownload = async (resource) => {
    try {
      setDownloadingResource(resource.id);
      setDownloadProgress(0);
      
      const url = getDownloadUrl(resource.id);
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }

      const contentLength = response.headers.get('content-length');
      const total = parseInt(contentLength, 10);
      let loaded = 0;

      const reader = response.body.getReader();
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        chunks.push(value);
        loaded += value.length;
        
        if (total) {
          setDownloadProgress(Math.round((loaded / total) * 100));
        }
      }

      const blob = new Blob(chunks);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = resource.file_name || resource.title || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      // Track download for files
      if (resource.type === 'file') {
        try {
          await trackProgress(resource.id, 'file', { downloaded: true });
          await fetchProgress(); // Refresh progress
        } catch (trackError) {
          console.error('Error tracking file download:', trackError);
        }
      }
      
      setDownloadingResource(null);
      setDownloadProgress(0);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Erreur lors du téléchargement');
      setDownloadingResource(null);
      setDownloadProgress(0);
    }
  };

  const handleVideoPlay = (resourceId) => {
    setPlayingResource(resourceId);
    setLoadingResource(resourceId);
    
    // If video element already exists and has a saved currentTime, restore it immediately
    const video = videoRefs.current[resourceId];
    const savedTime = videoCurrentTimes.current[resourceId];
    if (video && savedTime !== undefined && savedTime > 0) {
      // Wait for video to be ready, then restore time
      const restoreTime = () => {
        if (video.readyState >= 2) { // HAVE_CURRENT_DATA or higher
          video.currentTime = savedTime;
          delete videoCurrentTimes.current[resourceId];
        } else {
          video.addEventListener('loadeddata', () => {
            video.currentTime = savedTime;
            delete videoCurrentTimes.current[resourceId];
          }, { once: true });
        }
      };
      
      // If video is already loaded, restore immediately
      if (video.readyState >= 1) { // HAVE_METADATA or higher
        restoreTime();
      } else {
        // Wait for metadata to load
        video.addEventListener('loadedmetadata', restoreTime, { once: true });
      }
    }
  };

  const handleVideoLoaded = (resourceId) => {
    setLoadingResource(null);
    const video = videoRefs.current[resourceId];
    if (video) {
      // Initialize max time if not set
      if (maxVideoTimes.current[resourceId] === undefined) {
        maxVideoTimes.current[resourceId] = 0;
      }
      
      // Restore saved currentTime if it exists (from visibility pause)
      const savedTime = videoCurrentTimes.current[resourceId];
      if (savedTime !== undefined && savedTime > 0) {
        video.currentTime = savedTime;
        maxVideoTimes.current[resourceId] = savedTime; // Update max time to saved time
        delete videoCurrentTimes.current[resourceId];
        // Track progress with restored time
        trackProgress(resourceId, 'video', {
          progress_percent: video.duration ? Math.round((savedTime / video.duration) * 100) : 0,
          current_time: savedTime,
          duration: video.duration || 0
        });
      } else {
        // Track that video started (only if no saved time)
        maxVideoTimes.current[resourceId] = 0;
        trackProgress(resourceId, 'video', {
          progress_percent: 0,
          current_time: 0,
          duration: video.duration || 0
        });
      }
    }
  };

  const handleVideoTimeUpdate = (resourceId) => {
    const video = videoRefs.current[resourceId];
    if (video && video.duration) {
      const currentTime = video.currentTime;
      const maxTime = maxVideoTimes.current[resourceId] || 0;
      
      // Prevent skipping forward - enforce max time limit
      if (currentTime > maxTime + 0.5) { // Allow small buffer for buffering
        video.currentTime = maxTime;
        return; // Don't track progress if we had to correct the time
      }
      
      // Update max reached time only if we're playing forward naturally
      if (currentTime > maxTime) {
        maxVideoTimes.current[resourceId] = currentTime;
      }
      
      trackVideoProgress(resourceId, currentTime, video.duration);
    }
  };

  const handleVideoSeeking = (resourceId) => {
    const video = videoRefs.current[resourceId];
    if (video && maxVideoTimes.current[resourceId] !== undefined) {
      const maxTime = maxVideoTimes.current[resourceId];
      // Prevent seeking forward beyond the maximum reached time
      if (video.currentTime > maxTime) {
        video.currentTime = maxTime;
      }
    }
  };

  const handleVideoSeeked = (resourceId) => {
    const video = videoRefs.current[resourceId];
    if (video && maxVideoTimes.current[resourceId] !== undefined) {
      const maxTime = maxVideoTimes.current[resourceId];
      // Double-check after seeking completes to prevent any forward jumps
      if (video.currentTime > maxTime) {
        video.currentTime = maxTime;
      }
    }
  };

  const handleVideoEnded = (resourceId) => {
    const video = videoRefs.current[resourceId];
    if (video && video.duration) {
      // Track completion
      trackProgress(resourceId, 'video', {
        progress_percent: 100,
        current_time: video.duration,
        duration: video.duration
      });
    }
    setPlayingResource(null);
    fetchProgress(); // Refresh progress
  };

  const handleVideoError = (resourceId) => {
    setLoadingResource(null);
    alert('Erreur lors du chargement de la vidéo');
  };

  const handleAudioPlay = (resourceId) => {
    setPlayingResource(resourceId);
    setLoadingResource(resourceId);
  };

  const handleAudioLoaded = (resourceId) => {
    setLoadingResource(null);
    const audio = audioRefs.current[resourceId];
    if (audio) {
      // Initialize max time if not set
      if (maxAudioTimes.current[resourceId] === undefined) {
        maxAudioTimes.current[resourceId] = 0;
      }
      
      setAudioStates(prev => ({
        ...prev,
        [resourceId]: {
          ...prev[resourceId],
          duration: audio.duration || 0
        }
      }));
    }
  };

  const handleAudioError = (resourceId) => {
    setLoadingResource(null);
    alert('Erreur lors du chargement de l\'audio');
  };

  const toggleAudioPlay = async (resourceId) => {
    const audio = audioRefs.current[resourceId];
    if (!audio) return;

    // Prevent concurrent play/pause calls
    if (audioLoading[resourceId] || audioPlayPromises.current[resourceId]) {
      return;
    }

    // Initialize max time if not set
    if (maxAudioTimes.current[resourceId] === undefined) {
      maxAudioTimes.current[resourceId] = 0;
    }

    if (audio.paused) {
      // Set loading state to prevent multiple clicks
      setAudioLoading(prev => ({ ...prev, [resourceId]: true }));
      
      try {
        // Store the promise to prevent concurrent calls
        const playPromise = audio.play();
        audioPlayPromises.current[resourceId] = playPromise;
        
        // Wait for play to complete
        await playPromise;
        
        // Clear the promise reference
        delete audioPlayPromises.current[resourceId];
        
        // Clear loading state
        setAudioLoading(prev => {
          const newState = { ...prev };
          delete newState[resourceId];
          return newState;
        });
        
        // State will be updated by onPlay event handler
      } catch (error) {
        // Clear the promise reference on error
        delete audioPlayPromises.current[resourceId];
        
        // Clear loading state
        setAudioLoading(prev => {
          const newState = { ...prev };
          delete newState[resourceId];
          return newState;
        });
        
        // Handle the error silently if it's just an interruption
        if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
          console.error('Error playing audio:', error);
        }
        
        // Update state to reflect actual playback state
        setAudioStates(prev => ({
          ...prev,
          [resourceId]: { ...prev[resourceId], playing: !audio.paused }
        }));
      }
    } else {
      // If there's a pending play promise, wait for it first
      if (audioPlayPromises.current[resourceId]) {
        try {
          await audioPlayPromises.current[resourceId];
        } catch (error) {
          // Ignore errors from interrupted play
        }
        delete audioPlayPromises.current[resourceId];
      }
      
      audio.pause();
      // State will be updated by onPause event handler
    }
  };

  const handleAudioTimeUpdate = (resourceId) => {
    const audio = audioRefs.current[resourceId];
    if (audio) {
      const currentTime = audio.currentTime;
      const duration = audio.duration || 0;
      const maxTime = maxAudioTimes.current[resourceId] || 0;
      
      // Prevent skipping forward - enforce max time limit
      if (currentTime > maxTime + 0.5) { // Allow small buffer for buffering
        audio.currentTime = maxTime;
        // Update state to reflect the corrected time
        setAudioStates(prev => ({
          ...prev,
          [resourceId]: {
            ...prev[resourceId],
            currentTime: maxTime,
            duration: duration
          }
        }));
        return; // Don't track progress if we had to correct the time
      }
      
      // Update max reached time only if we're playing forward naturally
      if (currentTime > maxTime) {
        maxAudioTimes.current[resourceId] = currentTime;
      }
      
      setAudioStates(prev => ({
        ...prev,
        [resourceId]: {
          ...prev[resourceId],
          currentTime: currentTime,
          duration: duration
        }
      }));

      // Track progress
      if (duration > 0) {
        trackAudioProgress(resourceId, currentTime, duration);
      }
    }
  };

  const handleAudioProgressChange = (resourceId, newTime) => {
    const audio = audioRefs.current[resourceId];
    if (audio) {
      // Prevent seeking forward beyond the maximum reached time
      const maxTime = maxAudioTimes.current[resourceId] || 0;
      if (newTime > maxTime) {
        // Don't allow seeking forward, revert to max time
        audio.currentTime = maxTime;
        setAudioStates(prev => ({
          ...prev,
          [resourceId]: {
            ...prev[resourceId],
            currentTime: maxTime
          }
        }));
        return;
      }
      
      // Allow seeking backward or to current position
      audio.currentTime = newTime;
      setAudioStates(prev => ({
        ...prev,
        [resourceId]: {
          ...prev[resourceId],
          currentTime: newTime
        }
      }));
    }
  };

  const handleAudioEnded = (resourceId) => {
    const audio = audioRefs.current[resourceId];
    if (audio && audio.duration) {
      // Track completion
      trackProgress(resourceId, 'audio', {
        progress_percent: 100,
        current_time: audio.duration,
        duration: audio.duration
      });
    }
    setAudioStates(prev => ({
      ...prev,
      [resourceId]: {
        ...prev[resourceId],
        playing: false,
        currentTime: 0
      }
    }));
    setPlayingResource(null);
    fetchProgress(); // Refresh progress
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Chargement de la formation...</p>
        </div>
      </div>
    );
  }

  if (error || !formation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-xl shadow-lg p-8 max-w-md">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-6">{error || 'Formation non trouvée'}</p>
          <button
            onClick={() => navigate('/my-formations')}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Retour aux formations
          </button>
        </div>
      </div>
    );
  }

  const videos = resources.filter(r => r.type === 'video');
  const audios = resources.filter(r => r.type === 'audio');
  const files = resources.filter(r => r.type === 'file');
  const links = resources.filter(r => r.type === 'link');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 break-words">
            {formation.title}
          </h1>
          {formation.survey && (
            <p className="text-sm sm:text-base text-gray-600 break-words">
              Survey: {formation.survey.title}
            </p>
          )}
        </div>

        {/* Formation Description */}
        {formation.description && (
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-200">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Description</h2>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{formation.description}</p>
          </div>
        )}

        {/* Videos Section - One at a time */}
        {videos.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center mb-4 sm:mb-6 flex-wrap gap-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                <FontAwesomeIcon icon={faVideo} className="text-white text-sm sm:text-lg" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Vidéos</h2>
              <span className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-medium">
                {videos.length}
              </span>
            </div>
            <div className="space-y-6">
              {videos.map((video) => (
                <div key={video.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                  {/* Video Container - Fixed aspect ratio */}
                  <div className="relative bg-white border-b border-gray-200 w-full" style={{ aspectRatio: '16/9', minHeight: '200px', maxHeight: '100vh' }}>
                    {playingResource === video.id ? (
                      <>
                        {loadingResource === video.id && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white z-30">
                            <div className="text-center px-4">
                              <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-red-600 border-t-transparent mx-auto mb-3 sm:mb-4"></div>
                              <p className="text-gray-700 text-sm sm:text-lg font-medium">Veuillez patienter, chargement en cours...</p>
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-white" style={{ display: loadingResource === video.id ? 'none' : 'flex' }}>
                                  <video
                                    ref={(el) => (videoRefs.current[video.id] = el)}
                                    src={getStreamUrl(video.id)}
                                    controls
                                    preload="metadata"
                                    className="w-full h-full object-contain"
                                    onLoadedData={() => handleVideoLoaded(video.id)}
                                    onTimeUpdate={() => handleVideoTimeUpdate(video.id)}
                                    onSeeking={() => handleVideoSeeking(video.id)}
                                    onSeeked={() => handleVideoSeeked(video.id)}
                                    onError={() => handleVideoError(video.id)}
                                    onEnded={() => handleVideoEnded(video.id)}
                                    crossOrigin="anonymous"
                                    style={{ 
                                      backgroundColor: '#fff',
                                      display: loadingResource === video.id ? 'none' : 'block'
                                    }}
                                  >
                                    Votre navigateur ne supporte pas la lecture vidéo.
                                  </video>
                        </div>
                      </>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-white">
                        <button
                          onClick={() => handleVideoPlay(video.id)}
                          className="group relative focus:outline-none transform transition-transform hover:scale-105"
                        >
                          <div className="absolute inset-0 bg-red-600 rounded-full opacity-90 group-hover:opacity-100 transition-opacity transform scale-125 sm:scale-150"></div>
                          <FontAwesomeIcon 
                            icon={faPlay} 
                            className="relative text-white text-3xl sm:text-4xl ml-2 z-10"
                          />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Video Info */}
                  <div className="p-4 sm:p-6 bg-white">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{video.title || video.file_name}</h3>
                    {video.description && (
                      <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4">{video.description}</p>
                    )}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                      <div className="flex items-center flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm text-gray-500">
                        {video.duration_seconds && (
                          <span className="flex items-center">
                            <FontAwesomeIcon icon={faClock} className="mr-1 sm:mr-2 text-red-600 text-xs sm:text-sm" />
                            {formatDuration(video.duration_seconds)}
                          </span>
                        )}
                        {video.file_size && (
                          <span>{formatFileSize(video.file_size)}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleDownload(video)}
                        className="w-full sm:w-auto px-4 sm:px-5 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center font-medium shadow-sm text-sm sm:text-base"
                      >
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        Télécharger
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audio Section - One at a time, no autoplay */}
        {audios.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center mb-4 sm:mb-6 flex-wrap gap-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 shadow-sm flex-shrink-0">
                <FontAwesomeIcon icon={faHeadphones} className="text-white text-sm sm:text-lg" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Audio</h2>
              <span className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-medium">
                {audios.length}
              </span>
            </div>
            <div className="space-y-5">
              {audios.map((audio) => (
                <div key={audio.id} className="bg-white rounded-xl shadow-md border border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <div className="p-4 sm:p-6">
                    <div className="flex items-start">
                      <div className="flex-1 min-w-0">
                        <div className="mb-3 sm:mb-4">
                          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{audio.title || audio.file_name}</h3>
                          {audio.description && (
                            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{audio.description}</p>
                          )}
                        </div>
                        
                        {/* Audio Player - Custom styled */}
                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 sm:p-6 mb-3 sm:mb-4 border-2 border-gray-200 shadow-md">
                          {loadingResource === audio.id && (
                            <div className="flex items-center justify-center py-2 sm:py-3 mb-3 sm:mb-4 bg-red-50 rounded-lg border border-red-100">
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-red-600 mr-2 sm:mr-3 text-sm sm:text-lg" />
                              <span className="text-xs sm:text-sm font-medium text-gray-700">Veuillez patienter, chargement en cours...</span>
                            </div>
                          )}
                          
                          {/* Hidden audio element */}
                          <audio
                            ref={(el) => {
                              audioRefs.current[audio.id] = el;
                            }}
                            src={getStreamUrl(audio.id)}
                            preload="none"
                            className="hidden"
                            onTimeUpdate={() => handleAudioTimeUpdate(audio.id)}
                            onLoadedMetadata={() => handleAudioLoaded(audio.id)}
                            onPlay={() => {
                              setAudioStates(prev => ({
                                ...prev,
                                [audio.id]: { ...prev[audio.id], playing: true }
                              }));
                            }}
                            onPause={() => {
                              setAudioStates(prev => ({
                                ...prev,
                                [audio.id]: { ...prev[audio.id], playing: false }
                              }));
                            }}
                            onEnded={() => handleAudioEnded(audio.id)}
                            onError={() => handleAudioError(audio.id)}
                          >
                            Votre navigateur ne supporte pas la lecture audio.
                          </audio>

                          {/* Custom Audio Controls */}
                          <div className="flex items-center space-x-2 sm:space-x-5">
                            {/* Play/Pause Button */}
                            <button
                              onClick={() => toggleAudioPlay(audio.id)}
                              disabled={loadingResource === audio.id || audioLoading[audio.id]}
                              className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-full flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            >
                              <FontAwesomeIcon 
                                icon={audioStates[audio.id]?.playing ? faPause : faPlay} 
                                className="text-xl sm:text-2xl ml-0.5"
                              />
                            </button>

                            {/* Progress Bar */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 sm:space-x-4">
                                <span className="text-xs sm:text-sm font-semibold text-gray-700 w-12 sm:w-20 text-right tabular-nums flex-shrink-0">
                                  {formatDuration(audioStates[audio.id]?.currentTime || 0)}
                                </span>
                                <div className="flex-1 relative min-w-0" style={{ paddingTop: '8px', paddingBottom: '8px' }}>
                                  <input
                                    type="range"
                                    min="0"
                                    max={audioStates[audio.id]?.duration || 100}
                                    value={audioStates[audio.id]?.currentTime || 0}
                                    onChange={(e) => handleAudioProgressChange(audio.id, parseFloat(e.target.value))}
                                    disabled={!audioStates[audio.id]?.duration}
                                    className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer audio-slider disabled:cursor-not-allowed focus:outline-none"
                                    style={{
                                      background: `linear-gradient(to right, #dc2626 0%, #dc2626 ${audioStates[audio.id]?.duration ? ((audioStates[audio.id]?.currentTime || 0) / audioStates[audio.id].duration) * 100 : 0}%, #e5e7eb ${audioStates[audio.id]?.duration ? ((audioStates[audio.id]?.currentTime || 0) / audioStates[audio.id].duration) * 100 : 0}%, #e5e7eb 100%)`
                                    }}
                                  />
                                </div>
                                <span className="text-xs sm:text-sm font-semibold text-gray-700 w-12 sm:w-20 tabular-nums flex-shrink-0">
                                  {formatDuration(audioStates[audio.id]?.duration || 0)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm">
                            {audio.duration_seconds && (
                              <span className="flex items-center text-gray-600 font-medium">
                                <FontAwesomeIcon icon={faClock} className="mr-1 sm:mr-2 text-red-600 text-xs sm:text-sm" />
                                {formatDuration(audio.duration_seconds)}
                              </span>
                            )}
                            {audio.file_size && (
                              <span className="text-gray-500">{formatFileSize(audio.file_size)}</span>
                            )}
                          </div>
                          <button
                            onClick={() => handleDownload(audio)}
                            className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200 flex items-center justify-center font-medium shadow-sm hover:shadow-md text-sm sm:text-base"
                          >
                            <FontAwesomeIcon icon={faDownload} className="mr-2" />
                            Télécharger
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Files Section - Enhanced */}
        {files.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center mb-4 sm:mb-6 flex-wrap gap-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 shadow-sm flex-shrink-0">
                <FontAwesomeIcon icon={faFile} className="text-white text-sm sm:text-lg" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Fichiers</h2>
              <span className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-medium">
                {files.length}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {files.map((file) => {
                const isPdf = file.mime?.includes('pdf');
                const isImage = file.mime?.includes('image');
                const streamUrl = getStreamUrl(file.id);
                
                return (
                  <div key={file.id} className="bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-red-200 hover:shadow-2xl transition-all duration-300 overflow-hidden">
                    {/* File Header */}
                    <div className="bg-gradient-to-br from-red-50 via-white to-gray-50 p-4 sm:p-6 border-b-2 border-gray-200">
                      <div className="flex items-center justify-end mb-3 sm:mb-4">
                        {file.file_size && (
                          <span className="text-xs font-semibold text-gray-600 bg-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-gray-200 shadow-sm">
                            {formatFileSize(file.file_size)}
                          </span>
                        )}
                      </div>
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 line-clamp-2 mb-2" title={file.title || file.file_name}>
                        {file.title || file.file_name}
                      </h3>
                      {file.description && (
                        <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 leading-relaxed">{file.description}</p>
                      )}
                    </div>
                    
                    {/* Preview for PDFs and Images */}
                    {(isPdf || isImage) && (
                      <div className="border-b-2 border-gray-200 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
                        {isImage ? (
                          <div className="relative h-40 sm:h-56 overflow-hidden">
                            <img
                              src={streamUrl}
                              alt={file.title || file.file_name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        ) : (
                          <div className="h-40 sm:h-56 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-50 border-b-2 border-gray-200">
                            <iframe
                              src={streamUrl}
                              className="w-full h-full"
                              title={file.title || file.file_name}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Download Button */}
                    <div className="p-4 sm:p-5 bg-gradient-to-br from-gray-50 to-white">
                      <button
                        onClick={() => handleDownload(file)}
                        className="w-full px-4 sm:px-5 py-2.5 sm:py-3.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg transition-colors duration-200 flex items-center justify-center font-semibold shadow-md text-sm sm:text-base"
                      >
                        <FontAwesomeIcon icon={faDownload} className="mr-2 text-base sm:text-lg" />
                        Télécharger
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Links Section - Enhanced */}
        {links.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center mb-4 sm:mb-6 flex-wrap gap-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 shadow-sm flex-shrink-0">
                <FontAwesomeIcon icon={faLink} className="text-white text-sm sm:text-lg" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Liens externes</h2>
              <span className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-medium">
                {links.length}
              </span>
            </div>
            <div className="space-y-3 sm:space-y-4">
                      {links.map((link) => (
                        <div key={link.id} className="bg-white rounded-xl shadow-md border-2 border-gray-200 hover:border-red-200 hover:shadow-xl transition-all duration-300">
                          <a
                            href={link.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block p-4 sm:p-6 group"
                            onClick={() => {
                              trackProgress(link.id, 'link', { clicked: true });
                              fetchProgress();
                            }}
                          >
                    <div className="flex items-start sm:items-center justify-between gap-3 sm:gap-0">
                      <div className="flex-1 min-w-0 pr-2 sm:pr-4">
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                          {link.title || link.external_url}
                        </h3>
                        {link.is_meeting && link.meeting_at && (
                          <div className={`mb-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium ${
                            isMeetingPast(link.meeting_at)
                              ? 'bg-gray-100 text-gray-600 border border-gray-200'
                              : 'bg-red-50 text-red-700 border border-red-200'
                          }`}>
                            <FontAwesomeIcon icon={faCalendarDays} />
                            <span>
                              {isMeetingPast(link.meeting_at) ? 'Réunion passée : ' : 'Réunion en ligne : '}
                              {formatMeetingDate(link.meeting_at)}
                            </span>
                          </div>
                        )}
                        {link.description && (
                          <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 leading-relaxed">{link.description}</p>
                        )}
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={faLink} className="text-red-600 text-xs flex-shrink-0" />
                          <p className="text-xs text-gray-500 truncate font-mono">{link.external_url}</p>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors border-2 border-red-100 group-hover:border-red-200">
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-red-600 text-base sm:text-lg" />
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {resources.length === 0 && (
          <div className="bg-white rounded-xl shadow-md p-8 sm:p-12 text-center border border-gray-200">
            <FontAwesomeIcon icon={faFile} className="text-gray-400 text-4xl sm:text-6xl mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">Aucune ressource disponible</h3>
            <p className="text-sm sm:text-base text-gray-500">Cette formation ne contient pas encore de ressources.</p>
          </div>
        )}
      </div>

      {/* Download Progress Modal */}
      {downloadingResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 max-w-md w-full">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Téléchargement en cours</h3>
              <button
                onClick={() => {
                  setDownloadingResource(null);
                  setDownloadProgress(0);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-2">
                <span>Téléchargement...</span>
                <span className="font-medium text-red-600">{downloadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-red-500 to-red-600 h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${downloadProgress}%` }}
                ></div>
              </div>
            </div>
            
            <p className="text-xs sm:text-sm text-gray-500 text-center">
              Veuillez patienter pendant le téléchargement...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewFormation;
